import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import { useSession } from '../../permissions/SessionContext';
import { hasPermissions } from '../../permissions/check';
import { fetchClasses, fetchAttendance, saveAttendance } from '../../api/academy';
import { academyScope } from './scope';
import { colors, layout } from '../../design-system/tokens';

/** KST 오늘 'YYYY-MM-DD' (서버 UTC 자정으로 날짜가 밀리지 않게 Asia/Seoul 로 계산) */
const kstToday = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });

// 저장 가능한 상태만 토글로 노출한다. 'unmarked'(미기록)는 저장 상태가 아니다.
const STATUS_OPTIONS = [
  { value: 'present', label: '출석' },
  { value: 'late', label: '지각' },
  { value: 'absent', label: '결석' },
  { value: 'makeup', label: '보강' },
  { value: 'excused', label: '사유 인정' },
];

const STATUS_CHIP = {
  present: colors.status.success,
  late: colors.status.warning,
  absent: colors.status.danger,
  makeup: colors.status.info,
  excused: colors.status.info,
  unmarked: colors.status.neutral,
};
const STATUS_LABEL = {
  present: '출석',
  late: '지각',
  absent: '결석',
  makeup: '보강',
  excused: '사유 인정',
  unmarked: '미기록',
};
const entityId = (value) => String(value?.id ?? value?._id ?? value ?? '');

const Attendance = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  // 조회 권한만 있는 사용자에게는 저장 UI를 감춘다 (실제 차단은 backend attendance.manage)
  const canManage = hasPermissions(session, ['attendance.manage']);

  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [classesError, setClassesError] = useState('');
  const [classId, setClassId] = useState('');
  const [date, setDate] = useState(kstToday());

  const [roster, setRoster] = useState(null);
  const [edits, setEdits] = useState({}); // studentId -> status (로컬 변경분)
  const [noteEdits, setNoteEdits] = useState({}); // studentId -> 비민감 간단 메모
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState('');
  const [historyTarget, setHistoryTarget] = useState(null);

  // 반 목록 (선택용). 반이 하나면 자동 선택.
  // 조회 실패를 "반 없음"으로 오표시하지 않도록 로딩/오류 상태를 분리한다.
  const loadClasses = useCallback(async () => {
    setClassesLoading(true);
    setClassesError('');
    try {
      const list = await fetchClasses(academyScope(session));
      setClasses(list);
      if (list.length === 1) setClassId(entityId(list[0]));
    } catch (e) {
      setClassesError(e?.response?.data?.message || '반 목록을 불러오지 못했어요.');
    } finally {
      setClassesLoading(false);
    }
  }, [session]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const load = useCallback(async () => {
    if (!classId || !date) {
      setRoster(null);
      return;
    }
    setLoading(true);
    setError('');
    setSavedNote('');
    try {
      const data = await fetchAttendance({ classId, date, ...academyScope(session) });
      setRoster(data);
      setEdits({}); // 서버 상태를 기준으로 초기화
      setNoteEdits({});
    } catch (e) {
      setError(e?.response?.data?.message || '출결 현황을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [classId, date, session]);

  useEffect(() => {
    load();
  }, [load]);

  const statusOf = useCallback(
    (s) => edits[s.studentId] ?? s.status,
    [edits],
  );

  const setStatus = (studentId, value) => {
    if (!value) return; // exclusive 토글에서 같은 값 재클릭 시 null → 무시(선택 유지)
    setEdits((prev) => ({ ...prev, [studentId]: value }));
    setSavedNote('');
  };

  const applyStatusToAll = (value) => {
    if (!value || !roster?.students?.length) return;
    setEdits(
      Object.fromEntries(roster.students.map((student) => [student.studentId, value])),
    );
    setSavedNote('');
  };

  const dirty = useMemo(
    () => Object.keys(edits).length > 0 || Object.keys(noteEdits).length > 0,
    [edits, noteEdits],
  );

  const submit = async () => {
    if (!roster) return;
    setSaving(true);
    setError('');
    try {
      // 사용자가 실제로 바꾼 행만 보낸다. 손대지 않은 행을 다시 보내면 그 행의
      // recordedBy/변경시각이 이번 저장자로 덮어써지고 메모가 지워진다(누가 찍었는지 추적 훼손).
      const records = roster.students
        .filter(
          (s) =>
            Object.prototype.hasOwnProperty.call(edits, s.studentId) ||
            Object.prototype.hasOwnProperty.call(noteEdits, s.studentId),
        )
        .map((s) => ({
          studentId: s.studentId,
          status: statusOf(s), // 'unmarked' 는 서버가 저장에서 제외한다
          note: noteEdits[s.studentId] ?? s.note ?? '',
          expectedRevision: Number.isInteger(s.revision) ? s.revision : 0,
        }));
      const data = await saveAttendance({
        classId,
        attendanceDate: date,
        records,
        ...academyScope(session),
      });
      setRoster(data);
      setEdits({});
      setNoteEdits({});
      setSavedNote('출결을 저장했어요.');
    } catch (e) {
      setError(e?.response?.data?.message || '출결을 저장하지 못했어요.');
    } finally {
      setSaving(false);
    }
  };

  const students = roster?.students ?? [];

  return (
    <Box>
      <PageHeader
        title="출결 관리"
        description="반과 날짜를 고르고 원생별 출결을 기록해요. 아직 찍지 않은 원생은 '미기록'이며 결석이 아니에요. 사유 메모에는 건강·가정사 등 민감정보를 넣지 마세요."
      />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: layout.cardGap,
          mb: layout.sectionGap,
          alignItems: 'center',
        }}
      >
        <TextField
          select
          size="small"
          label="반 선택"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          sx={{ minWidth: 220 }}
          disabled={classes.length === 0}
        >
          {classes.map((c) => (
            <MenuItem key={entityId(c)} value={entityId(c)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          size="small"
          label="날짜"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 170 }}
        />
        {canManage && roster && students.length > 0 && (
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving || !dirty}
            sx={{ ml: { sm: 'auto' }, flexShrink: 0 }}
          >
            {saving ? '저장 중…' : '저장'}
          </Button>
        )}
      </Box>

      {canManage && roster && students.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: layout.sectionGap,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
          role="group"
          aria-label="전체 원생 출결 일괄 입력"
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mr: 0.5 }}>
            전체 적용
          </Typography>
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="small"
              variant="outlined"
              onClick={() => applyStatusToAll(option.value)}
            >
              전체 {option.label}
            </Button>
          ))}
          <Typography variant="caption" sx={{ color: colors.text.muted, ml: { sm: 'auto' } }}>
            적용 후 저장해야 반영돼요.
          </Typography>
        </Paper>
      )}

      {classesError ? (
        <Paper
          sx={{ p: layout.cardPadding, textAlign: 'center', mb: layout.sectionGap }}
        >
          <Typography variant="body2" sx={{ color: colors.status.danger.fg, mb: 2 }}>
            {classesError}
          </Typography>
          <Button variant="contained" onClick={loadClasses}>
            다시 시도
          </Button>
        </Paper>
      ) : (
        !classesLoading &&
        classes.length === 0 && (
          <Alert severity="info" sx={{ mb: layout.sectionGap }}>
            먼저 <b>수업 관리</b>에서 반을 등록하면 출결을 기록할 수 있어요.
            <Button size="small" onClick={() => navigate('/owner-v2/classes')} sx={{ ml: 1 }}>
              수업 관리 열기
            </Button>
          </Alert>
        )
      )}

      {savedNote && (
        <Alert role="status" severity="success" sx={{ mb: layout.sectionGap }}>
          {savedNote}
        </Alert>
      )}

      {!classId ? (
        classes.length > 0 && (
          <Paper sx={{ p: layout.cardPadding + 1, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: colors.text.muted }}>
              반을 선택하면 재원 중인 원생의 출결을 볼 수 있어요.
            </Typography>
          </Paper>
        )
      ) : (
        <DataState
          loading={loading}
          error={error}
          onRetry={load}
          empty={students.length === 0}
          emptyText="이 반에 재원 중인 원생이 없어요."
        >
          <Paper sx={{ p: 1 }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>이름</TableCell>
                    <TableCell>학년</TableCell>
                    <TableCell>출결</TableCell>
                    <TableCell>간단 메모</TableCell>
                    <TableCell>변경 기록</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((s) => {
                    const cur = statusOf(s);
                    return (
                      <TableRow key={s.studentId} hover>
                        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {s.name}
                        </TableCell>
                        <TableCell sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
                          {s.grade || '-'}
                        </TableCell>
                        <TableCell>
                          {canManage ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={cur === 'unmarked' ? null : cur}
                                onChange={(e, v) => setStatus(s.studentId, v)}
                                aria-label={`${s.name} 출결`}
                              >
                                {STATUS_OPTIONS.map((o) => (
                                  <ToggleButton key={o.value} value={o.value}>
                                    {o.label}
                                  </ToggleButton>
                                ))}
                              </ToggleButtonGroup>
                              {cur === 'unmarked' && (
                                <Typography variant="caption" sx={{ color: colors.text.muted }}>
                                  미기록
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Chip
                              size="small"
                              label={STATUS_LABEL[cur] || '상태 확인 필요'}
                              sx={{
                                bgcolor: (STATUS_CHIP[cur] || colors.status.neutral).bg,
                                color: (STATUS_CHIP[cur] || colors.status.neutral).fg,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          {canManage ? (
                            <TextField
                              size="small"
                              value={noteEdits[s.studentId] ?? s.note ?? ''}
                              onChange={(event) => {
                                const value = event.target.value.slice(0, 200);
                                setNoteEdits((prev) => ({ ...prev, [s.studentId]: value }));
                                setSavedNote('');
                              }}
                              placeholder={cur === 'unmarked' ? '출결을 먼저 선택하세요' : '비민감 메모만 입력'}
                              disabled={cur === 'unmarked'}
                              slotProps={{
                                htmlInput: {
                                  'aria-label': `${s.name} 출결 간단 메모`,
                                  maxLength: 200,
                                },
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.text.muted }}>
                              {s.note || '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          {s.recordedAt ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                                {new Date(s.recordedAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
                              </Typography>
                              {Array.isArray(s.history) && s.history.length > 0 && (
                                <Button size="small" sx={{ minWidth: 0, px: 0 }} onClick={() => setHistoryTarget(s)}>
                                  변경 이력 {s.history.length}건
                                </Button>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: colors.text.muted }}>
                              기록 없음
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </DataState>
      )}

      {classId && !loading && !error && students.length === 0 && canManage && (
        <Alert severity="info" sx={{ mt: layout.cardGap }}>
          이 반에 배정된 재원 원생이 없어요. 수강생을 배정한 뒤 출결을 기록할 수 있어요.
          <Button size="small" onClick={() => navigate('/owner-v2/students')} sx={{ ml: 1 }}>
            수강생 관리 열기
          </Button>
        </Alert>
      )}

      <Dialog open={Boolean(historyTarget)} onClose={() => setHistoryTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>{historyTarget?.name} 출결 변경 이력</DialogTitle>
        <DialogContent>
          {(historyTarget?.history ?? []).length === 0 ? (
            <Typography variant="body2" sx={{ color: colors.text.muted }}>
              별도 변경 이력이 없어요.
            </Typography>
          ) : (
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              {historyTarget.history.map((item, index) => (
                <Box component="li" key={`${item.occurredAt}-${index}`} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {STATUS_LABEL[item.previousStatus] || '미기록'} → {STATUS_LABEL[item.nextStatus] || '미기록'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.text.muted }}>
                    {new Date(item.occurredAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
                    {' · '}{item.actorLabel || '관리자'}
                    {item.noteChanged ? ' · 메모 변경' : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryTarget(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attendance;
